import { t } from "@lingui/macro";
import { AnalyzeResumeResult } from "@reactive-resume/schema";
import { useTheme } from "@reactive-resume/hooks";
import { cn } from "@reactive-resume/utils";
import {
  CheckCircle2,
  XCircle,
  InfoIcon,
  Target,
  AlertTriangle,
  ArrowRight,
  Plus,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useState } from "react";

type KeywordInContextIntegrityProps = {
  analysesData: AnalyzeResumeResult;
  className?: string;
};

export const KeywordInContextIntegrity = ({
  analysesData,
  className,
}: KeywordInContextIntegrityProps) => {
  const { isDarkMode } = useTheme();
  const [showAllStrong, setShowAllStrong] = useState(false);
  const [showAllWeak, setShowAllWeak] = useState(false);
  const [showAllMissing, setShowAllMissing] = useState(false);

  // Initial number of items to show
  const INITIAL_ITEMS_TO_SHOW = 8;

  // Return null if data doesn't exist
  if (!analysesData.metrics.kici || !analysesData.metrics.kici.object) {
    return null;
  }

  const { jd_keywords, resume_bullets } = analysesData.metrics.kici.object;

  // Calculate metrics
  const keywordsWithContext: string[] = [];
  const keywordsWithoutContext: string[] = [];

  // Process each bullet to identify keywords with proper context
  jd_keywords.forEach((keyword) => {
    let foundWithContext = false;
    let foundWithoutContext = false;

    resume_bullets.forEach((bullet) => {
      if (bullet.keywords.includes(keyword)) {
        if (bullet.has_action && bullet.has_outcome) {
          if (!foundWithContext) {
            keywordsWithContext.push(keyword);
            foundWithContext = true;
          }
        } else if (!foundWithContext && !foundWithoutContext) {
          keywordsWithoutContext.push(keyword);
          foundWithoutContext = true;
        }
      }
    });
  });

  // Find keywords that don't appear in any bullets
  const missingKeywords = jd_keywords.filter(
    (keyword) =>
      !keywordsWithContext.includes(keyword) && !keywordsWithoutContext.includes(keyword),
  );

  // Get percentage from analysis results - same calculation as metric-score-card.tsx
  const score =
    analysesData.metrics.kici.weight > 0
      ? analysesData.metrics.kici.weightedScore / analysesData.metrics.kici.weight
      : 0;
  const percentWithContext = Math.round(score * 100);

  // Format keyword for display
  const formatKeyword = (keyword: string) => {
    return keyword;
  };

  // Define colors - unified with other components
  const colors = {
    green: isDarkMode ? "#66bb6a" : "#81c784",
    amber: isDarkMode ? "#ff9800" : "#f57c00",
    red: isDarkMode ? "#ef5350" : "#e57373",
    blue: "#4dabf5",
    greenBg: isDarkMode ? "rgba(102, 187, 106, 0.15)" : "rgba(129, 199, 132, 0.1)",
    amberBg: isDarkMode ? "rgba(255, 152, 0, 0.15)" : "rgba(245, 124, 0, 0.1)",
    redBg: isDarkMode ? "rgba(239, 83, 80, 0.15)" : "rgba(229, 115, 115, 0.1)",
    blueBg: isDarkMode ? "rgba(77, 171, 245, 0.15)" : "rgba(77, 171, 245, 0.1)",
  };

  // Determine status based on percentage
  let status: "excellent" | "good" | "needs_work" = "needs_work";
  if (percentWithContext >= 80) status = "excellent";
  else if (percentWithContext >= 60) status = "good";

  // Get status information
  const getStatusInfo = () => {
    switch (status) {
      case "excellent":
        return {
          text: t`Excellent`,
          description: t`Most of your keywords are backed by strong examples with action verbs and measurable results.`,
          action: t`Keep up the great work - your keywords have excellent context!`,
          bgColor: colors.greenBg,
          textColor: colors.green,
        };
      case "good":
        return {
          text: t`Good`,
          description: t`Many of your keywords have good context, but some could be strengthened.`,
          action: t`Review the suggestions below to make your keywords even stronger.`,
          bgColor: colors.blueBg,
          textColor: colors.blue,
        };
      default:
        return {
          text: t`Needs Work`,
          description: t`Many keywords lack strong context with action verbs and measurable outcomes.`,
          action: t`Focus on strengthening your keyword usage with the recommendations below.`,
          bgColor: colors.redBg,
          textColor: colors.red,
        };
    }
  };

  const statusInfo = getStatusInfo();

  // Get keywords to display based on show all state
  const strongKeywordsToShow = showAllStrong
    ? keywordsWithContext
    : keywordsWithContext.slice(0, INITIAL_ITEMS_TO_SHOW);
  const weakKeywordsToShow = showAllWeak
    ? keywordsWithoutContext
    : keywordsWithoutContext.slice(0, INITIAL_ITEMS_TO_SHOW);
  const missingKeywordsToShow = showAllMissing
    ? missingKeywords
    : missingKeywords.slice(0, INITIAL_ITEMS_TO_SHOW);

  return (
    <div className={cn("grid gap-y-6", className)}>
      <div className="grid gap-y-5">
        {/* Status Overview */}
        <div
          className="rounded-lg p-4 transition-colors"
          style={{
            backgroundColor: statusInfo.bgColor,
            borderLeft: `4px solid ${statusInfo.textColor}`,
          }}
        >
          <div className="mb-2 flex items-center gap-x-2">
            <InfoIcon className="h-5 w-5" style={{ color: statusInfo.textColor }} />
            <span className="text-lg font-semibold" style={{ color: statusInfo.textColor }}>
              {t`Keyword Context Integrity`}: {statusInfo.text} ({percentWithContext}%)
            </span>
          </div>
          <p className="mb-2 text-sm text-slate-700 dark:text-slate-300">
            {statusInfo.description}
          </p>
          <div
            className="flex items-center gap-x-2 text-sm font-medium"
            style={{ color: statusInfo.textColor }}
          >
            <ArrowRight className="h-4 w-4" />
            <span>{statusInfo.action}</span>
          </div>
        </div>

        {/* Keywords with Strong Context */}
        {keywordsWithContext.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                {t`You Have These (${keywordsWithContext.length})`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`These keywords appear with both action verbs AND measurable results. Great job!`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {strongKeywordsToShow.map((keyword) => (
                <div
                  key={keyword}
                  className="flex items-center gap-x-2 rounded-lg border px-3 py-2 text-sm font-medium"
                  style={{
                    backgroundColor: colors.greenBg,
                    borderColor: colors.green,
                    color: colors.green,
                  }}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  <span>{formatKeyword(keyword)}</span>
                </div>
              ))}
              {keywordsWithContext.length > INITIAL_ITEMS_TO_SHOW && (
                <button
                  onClick={() => setShowAllStrong(!showAllStrong)}
                  className="mt-3 flex w-full items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  {showAllStrong ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      {t`Show Less`}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      {t`Show ${keywordsWithContext.length - INITIAL_ITEMS_TO_SHOW} More`}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Missing Keywords */}
        {missingKeywords.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <XCircle className="h-5 w-5 text-red-600" />
                {t`You Need These (${missingKeywords.length})`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`These important keywords from the job description don't appear in your resume at all.`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {missingKeywordsToShow.map((keyword) => (
                <div
                  key={keyword}
                  className="flex items-center gap-x-2 rounded-lg border px-3 py-2 text-sm font-medium"
                  style={{
                    backgroundColor: colors.redBg,
                    borderColor: colors.red,
                    color: colors.red,
                  }}
                >
                  <Plus className="h-4 w-4" />
                  <span>{formatKeyword(keyword)}</span>
                </div>
              ))}
              {missingKeywords.length > INITIAL_ITEMS_TO_SHOW && (
                <button
                  onClick={() => setShowAllMissing(!showAllMissing)}
                  className="mt-3 flex w-full items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  {showAllMissing ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      {t`Show Less`}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      {t`Show ${missingKeywords.length - INITIAL_ITEMS_TO_SHOW} More`}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Keywords Missing Context */}
        {keywordsWithoutContext.length > 0 && (
          <div>
            <div className="mb-3">
              <h4 className="mb-1 flex items-center gap-x-2 text-base font-semibold">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                {t`You Need to Strengthen (${keywordsWithoutContext.length})`}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`These keywords appear in your resume but need stronger context. Add action verbs and measurable results.`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {weakKeywordsToShow.map((keyword) => (
                <div
                  key={keyword}
                  className="flex items-center gap-x-2 rounded-lg border px-3 py-2 text-sm font-medium"
                  style={{
                    backgroundColor: colors.amberBg,
                    borderColor: colors.amber,
                    color: colors.amber,
                  }}
                >
                  <AlertTriangle className="h-4 w-4" />
                  <span>{formatKeyword(keyword)}</span>
                </div>
              ))}
              {keywordsWithoutContext.length > INITIAL_ITEMS_TO_SHOW && (
                <button
                  onClick={() => setShowAllWeak(!showAllWeak)}
                  className="mt-3 flex w-full items-center justify-center gap-x-2 rounded-lg border border-slate-200 bg-white p-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  {showAllWeak ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      {t`Show Less`}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      {t`Show ${keywordsWithoutContext.length - INITIAL_ITEMS_TO_SHOW} More`}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* No data found */}
        {keywordsWithContext.length === 0 &&
          keywordsWithoutContext.length === 0 &&
          missingKeywords.length === 0 && (
            <div className="rounded-lg bg-slate-50 p-4 text-center dark:bg-slate-800/50">
              <InfoIcon className="mx-auto mb-2 h-8 w-8 text-slate-400" />
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t`No keyword data available for analysis.`}
              </p>
            </div>
          )}
      </div>
    </div>
  );
};
